import {
  Route,
  useLocation,
  Navigate,
  useParams,
  useNavigate,
  createRoutesFromElements,
  RouterProvider,
  useRouteError,
  useSearchParams,
} from 'react-router-dom';
import { Box, ChakraProvider } from '@chakra-ui/react';
import React, { createContext, Suspense, useEffect, useMemo, useState } from 'react';
import { newTheme, theme } from 'theme';
import {
  GENERAL_MODULES,
  GROUP_COMPANY_MODULES_SECTION_1,
  GROUP_COMPANY_MODULES_SECTION_2,
  Navigation,
  PORTFOLIO_COMPANY_MODULES_SECTION_1,
  REGULAR_COMPANY_MODULES_SECTION_1,
  REGULAR_COMPANY_MODULES_SECTION_2,
  REGULAR_COMPANY_MODULES_SECTION_3,
} from 'containers/Navigation';
import { hotjar } from 'react-hotjar';
import mixpanel from 'mixpanel-browser';
import { isProduction } from 'utils/env';
import TagManager from 'react-gtm-module';
import {
  CookieConsentModal,
  useAcceptAllInvitations,
  useCookieConsent,
  usePendingInvitations,
} from 'Features';
import { Login } from 'containers/Authentication';
import {
  CompanyType,
  useCompanyType,
  useCurrentCompanyId,
  useUserCompanyCheck,
  useToast,
  useRegisterMixpanelCompanyDetails,
} from 'utils/hooks';
import { useTranslation } from 'utils/translation';
import 'utils/translation/i18n';
import { ContentLayout, Loader } from 'Molecules';
import { CompanyRegisterModal } from 'Features/CompanyForm';
import ChangePassword, { VerifyLinkPage } from 'containers/Authentication/ChangePassword';
import { JoinCompany } from 'containers/JoinCompany';
import { SSOLogin, NoCompanyWelcomeModal } from 'containers/Authentication/SSOLogin';
import { hasSalesOrSupportRole } from 'utils/users';
import { Home } from 'containers/Home';
import { EmptyState } from 'Atoms';
import { ErrorIllustration, NotFoundIllustration } from 'Tokens';
import { SentryRoutes as Routes, sentryCreateBrowserRouter } from 'utils/sentry';
import { captureException } from '@sentry/browser';
import { WebsocketTest } from 'containers/WebsocketTest';
import { CompassLogin } from 'containers/Authentication/CompassLogin';
import { Settings } from 'containers/Settings';
import { nhost, NhostProvider, useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { AuthProvider, useAuth } from 'Providers/auth/AuthProvider.tsx';
import { NhostApolloProvider } from 'Providers/apollo/NhostApolloProvider';
// import { Maintenance } from 'containers/Maintenance';

// const DOWN_FOR_MAINTENANCE = true;

const hotjarId = import.meta.env?.REACT_APP_HOTJAR_ID;
const hotjarSv = import.meta.env?.REACT_APP_HOTJAR_SV;
const mixpanelId = import.meta.env?.REACT_APP_MIXPANEL_ID;

const tagManagerArgs = {
  gtmId: import.meta.env?.REACT_APP_GOOGLE_TAG_MANAGER_ID ?? '',
};

const Orientation = () => {
  const { t } = useTranslation(['common']);
  const { companyId, error } = useCurrentCompanyId();
  const { user } = useAuth();

  if (error) {
    captureException(error, {
      extra: { errorMessage: 'Error fetching user current company id' },
    });
    return <ErrorElement />;
  }
  if (companyId) return <Navigate to={`/${companyId}`} />;

  return (
    <>
      {hasSalesOrSupportRole(user?.roles) ? (
        <ContentLayout header="">
          <CompanyRegisterModal isOpen={true} onClose={() => {}} header={t('common:app.setup')} />
        </ContentLayout>
      ) : (
        <NoCompanyWelcomeModal />
      )}
    </>
  );
};

const CompanyController = ({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation(['login', 'common']);
  const { companyId: urlCompanyId } = useParams();
  const { hasCompany: checkAccess } = useUserCompanyCheck();
  const hasAccess = useMemo(() => checkAccess(urlCompanyId), [checkAccess, urlCompanyId]);
  const { user } = useAuth();
  const toast = useToast();

  const { invitations } = usePendingInvitations({ userEmail: user?.email ?? '' });

  const pendingInvitations =
    invitations?.filter((invitation) => invitation.status === 'pending') ?? [];

  const [acceptInvitations] = useAcceptAllInvitations();
  useEffect(() => {
    if (pendingInvitations.length && user) {
      acceptInvitations(invitations ?? [], user).then((res) => {
        if (res.body.companiesAdded) {
          toast({
            text: t('login:joiningCompanies.success', {
              count: res.body.companiesAdded,
            }),
          });
        }
      });
    }
  }, [pendingInvitations, user]);

  if (invitations === undefined || invitations === null || (pendingInvitations.length && user)) {
    return <Loader label={t('login:joiningCompanies')} />;
  }

  if (!hasAccess) {
    return <Orientation />;
  }

  return <Suspense fallback={<Loader />}>{children}</Suspense>;
};

const ProtectedRoute = ({ children }: { children: any }) => {
  const [isValidSession, setIsValidSession] = useState(true);
  const [isValidatingSession, setIsValidatingSession] = useState(false);
  const { loading } = useCurrentCompanyId();
  const [searchParams] = useSearchParams();
  const refreshToken = searchParams.get('refreshToken');
  const nhost = useNhostClient();

  const { isAuthenticated, isLoading, session } = useAuth();
  const location = useLocation();

  useEffect(() => {
    const refreshTokenHandler = async () => {
      if (refreshToken) {
        try {
          await nhost.auth.refreshToken({ refreshToken });
        } catch (e) {
          captureException(e);
        }
      }
    };
    refreshTokenHandler();
  }, [refreshToken]);

  useEffect(() => {
    let cancelled = false;

    const validateAndTryRefresh = async () => {
      if (!session) return;
      setIsValidatingSession(true);

      // start validation

      // Try initial validation
      try {
        const res = await nhost.functions.post('/validate-session', {
          tokenId: session?.refreshTokenId,
        });

        if (!cancelled && res?.status === 200) {
          setIsValidSession(true);
          setIsValidatingSession(false);
          return;
        }
      } catch (e) {
        captureException(e);
      }

      // Attempt a single refresh before giving up (use refreshToken API)
      try {
        if (session?.refreshToken && typeof nhost.auth?.refreshToken === 'function') {
          await nhost.auth.refreshToken({ refreshToken: session.refreshToken });
        } else {
          throw new Error('No refresh method available');
        }

        // Re-validate using the refreshed session
        const newSession = nhost.getUserSession();

        const res2 = await nhost.functions.post('/validate-session', {
          tokenId: newSession?.refreshTokenId,
        });

        if (!cancelled && res2?.status === 200) {
          setIsValidSession(true);
          return;
        }

        console.warn('[ProtectedRoute] re-validation did not return 200', { status: res2?.status });
      } catch (e) {
        console.error('[ProtectedRoute] refresh or re-validation failed — clearing session', e);
        captureException(e);
        nhost.clearSession();
        setIsValidSession(false);
      } finally {
        if (!cancelled) setIsValidatingSession(false);
      }
    };

    validateAndTryRefresh();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, session?.accessToken, session?.refreshToken, session?.refreshTokenId]);

  if (loading || isLoading || isValidatingSession) {
    return <Loader height="100vh" />;
  }

  if ((!isAuthenticated && !refreshToken) || !isValidSession) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <CompanyController>{children}</CompanyController>;
};

const PageNotFound = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  return (
    <Box w="100%" flexGrow="1" p="16px 16px 0px">
      <EmptyState
        title={t('common:notFound.title')}
        description={t('common:notFound.description')}
        callToAction={{
          text: t('common:notFound.button'),
          variant: 'primary',
          onClick: () => {
            navigate('/');
          },
        }}
        icon={<NotFoundIllustration boxSize="120px" />}
      />
    </Box>
  );
};

export const ErrorElement = () => {
  const { t } = useTranslation('common');
  const error = useRouteError();

  useEffect(() => {
    if (error) {
      captureException(error);
    }
  }, [error]);

  return (
    <Box w="100%" h="100vh" p="16px">
      <EmptyState
        title={t('common:errorElement.title')}
        description={t('common:errorElement.description')}
        callToAction={{
          text: t('common:errorElement.button'),
          variant: 'secondary',
          onClick: () => {
            window.location.reload();
          },
        }}
        icon={<ErrorIllustration boxSize="120px" />}
      />
    </Box>
  );
};

const Layout = () => {
  const { companyType, loading } = useCompanyType();
  const registerMixpanel = useRegisterMixpanelCompanyDetails();
  const location = useLocation();

  registerMixpanel();
  const moduleRoutes = useMemo(() => {
    if (companyType === CompanyType.regular) {
      return [
        ...REGULAR_COMPANY_MODULES_SECTION_1,
        ...REGULAR_COMPANY_MODULES_SECTION_2,
        ...REGULAR_COMPANY_MODULES_SECTION_3,
        ...GENERAL_MODULES,
      ];
    } else if (companyType === CompanyType.portfolio) {
      return [...PORTFOLIO_COMPANY_MODULES_SECTION_1, ...GENERAL_MODULES];
    }
    return [
      ...GROUP_COMPANY_MODULES_SECTION_1,
      ...GROUP_COMPANY_MODULES_SECTION_2,
      ...GENERAL_MODULES,
    ];
  }, [companyType]);

  const RouteElement = () => {
    if (companyType === CompanyType.regular || companyType === CompanyType.group) {
      return <Home />;
    } else if (companyType === CompanyType.portfolio) {
      return <Navigate to="portfolios" />;
    }
  };

  const RedirectToSustainabilityReporting = () => {
    const sustainabilityReportingPath = location.pathname.replace(
      /\/esrs/,
      '/sustainability-reporting/esrs'
    );

    return <Navigate to={sustainabilityReportingPath + location.search + location.hash} replace />;
  };

  return (
    <Navigation>
      <Loader isLoaded={!loading}>
        <Routes>
          <Route path="/" element={<RouteElement />} errorElement={<ErrorElement />} />
          <Route
            path="/esrs/*"
            element={<RedirectToSustainabilityReporting />}
            errorElement={<ErrorElement />}
          />
          {moduleRoutes.map((module) => (
            <Route
              path={`/${module.path}/*`}
              element={module.component}
              key={module.path}
              errorElement={<ErrorElement />}
            />
          ))}
          <Route
            path={`/settings/:currentTab`}
            element={<Settings />}
            key={'settings'}
            errorElement={<ErrorElement />}
          />
          <Route path="*" element={<PageNotFound />}></Route>
        </Routes>
      </Loader>
    </Navigation>
  );
};

const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <>
      <Route
        path="password-reset-verification"
        element={<VerifyLinkPage />}
        errorElement={<ErrorElement />}
      />
      {/* <Route path="hidden-sign-up-page" element={<SignUp />} errorElement={<ErrorElement />} /> */}
      <Route path="login" element={<Login />} errorElement={<ErrorElement />} />
      <Route path="compass-login" element={<CompassLogin />} errorElement={<ErrorElement />} />
      <Route path="sso-login" element={<SSOLogin />} errorElement={<ErrorElement />} />
      <Route path="changePassword" element={<ChangePassword />} errorElement={<ErrorElement />} />
      <Route path="join-company" element={<JoinCompany />} errorElement={<ErrorElement />} />
      <Route path="invited" element={<Orientation />} errorElement={<ErrorElement />} />
      <Route path="websocket-test" element={<WebsocketTest />} errorElement={<ErrorElement />} />
      <Route
        errorElement={<ErrorElement />}
        path="/"
        element={
          <ProtectedRoute>
            <Orientation />
          </ProtectedRoute>
        }
      />
      <Route
        errorElement={<ErrorElement />}
        path="/:companyId/*"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      />
    </>
  )
);

export const appContext = createContext({
  usedColorMode: '',
  setUsedColorMode: (_val: string) => {},
});

function App() {
  const { consent } = useCookieConsent();
  const [currentColorMode, setCurrentColorMode] = useState('');

  useEffect(() => {
    try {
      // As long as we are only using hotjar for surveys, and have session recording turned off, consent is not needed.
      hotjar.initialize({ id: Number(hotjarId), sv: Number(hotjarSv) });
    } catch (e) {
      captureException(e);
    }

    if (consent.analytics && isProduction()) {
      TagManager.initialize(tagManagerArgs);
    }

    mixpanel.init(mixpanelId ?? '');
    if (consent.analytics) {
      mixpanel.opt_in_tracking();
    } else {
      mixpanel.opt_out_tracking();
    }
  }, []);

  // Uncomment to set maintenance on
  // if (DOWN_FOR_MAINTENANCE) {
  //   return (
  //     <ChakraProvider theme={theme}>
  //       <Maintenance />
  //     </ChakraProvider>
  //   );
  // }

  const context = {
    usedColorMode: currentColorMode,
    setUsedColorMode: setCurrentColorMode,
  };

  return (
    <>
      <link href="/custom-components-styles.css" rel="stylesheet" type="text/css" />
      <appContext.Provider value={context}>
        <ChakraProvider theme={currentColorMode === 'new' ? newTheme : theme}>
          <NhostProvider nhost={nhost}>
            <AuthProvider>
              <NhostApolloProvider>
                <Suspense fallback={<Loader />}>
                  <RouterProvider router={router} />
                </Suspense>
                <CookieConsentModal />
              </NhostApolloProvider>
            </AuthProvider>
          </NhostProvider>
        </ChakraProvider>
      </appContext.Provider>
    </>
  );
}

export default App;
